import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { I18nContext } from '../context/translations/TranslationsContext.context';
import { AccesoGuardado, borrarAcceso, recuperarAcceso } from './accesoGuardado';
import { TipoDeBiometria, tipoDeBiometria } from './biometria';
import { biometriaActivada, olvidarBiometria, tocaSaltarFaceId } from './preferenciaBiometria';
import { haySesionAbierta } from './sesion';
import { esApp } from './plataforma';
import { textosParaEntrar } from './textosBiometria';

/** Cómo le fue al login con el acceso guardado. Solo `credenciales` lo invalida. */
export type ResultadoDeEntrar = 'ok' | 'credenciales' | 'otro';

export interface EntrarConBiometria {
	/** Con qué se entra (para el botón «Entrar con Face ID»), o `null` si no se ofrece. */
	tipo: TipoDeBiometria | null;
	/** Lo que hace el botón: pide la cara, rellena y entra. */
	entrarConBiometria: () => void;
}

/**
 * En la pantalla de login de la app: si el usuario activó «Entrar con Face ID», pide la cara o la
 * huella nada más llegar, rellena el NIF y la contraseña guardados y entra. Y ofrece un botón para
 * hacer lo mismo cuando quiera: tras cerrar sesión la cara no se pide sola
 * (`noEntrarConFaceIdTrasCerrarSesion`), y sin botón habría que escribir la contraseña.
 *
 * - Si cancela, se queda en el formulario; lo guardado sigue y el botón también.
 * - Si ya no hay nada utilizable (el sistema lo invalidó al cambiar la biometría) o la API lo
 *   rechaza (cambió la contraseña), se borra, el botón desaparece y se le volverá a ofrecer al
 *   entrar con contraseña.
 * - Un fallo de otro tipo (red, mantenimiento) no toca lo guardado.
 *
 * La petición automática, una vez por pantalla. En la web, o si no lo tiene activado, no hace nada.
 */
export const useEntrarConBiometria = (
	activo: boolean,
	entrar: (acceso: AccesoGuardado) => Promise<ResultadoDeEntrar>
): EntrarConBiometria => {
	const { translate } = useContext(I18nContext).state;
	const [tipo, setTipo] = useState<TipoDeBiometria | null>(null);
	const yaIntentado = useRef(false);
	const enCurso = useRef(false);
	const ultimo = useRef({ translate, entrar });
	ultimo.current = { translate, entrar };

	const intentar = useCallback(async () => {
		if (enCurso.current) {
			return;
		}
		enCurso.current = true;

		try {
			const { translate: traducir } = ultimo.current;
			const acceso = await recuperarAcceso(textosParaEntrar(traducir));

			if (acceso === 'cancelado') {
				return;
			}
			if (acceso === null || (await ultimo.current.entrar(acceso)) === 'credenciales') {
				await borrarAcceso();
				olvidarBiometria();
				setTipo(null);
			}
		} finally {
			enCurso.current = false;
		}
	}, []);

	useEffect(() => {
		// Con la sesión aún abierta la cara la pide el bloqueo (`BloqueoBiometrico`): aquí no, o
		// saldrían dos peticiones a la vez al abrir la app.
		if (yaIntentado.current || !activo || !esApp() || haySesionAbierta()) {
			return;
		}
		yaIntentado.current = true;

		// La marca de «cerrar sesión» es de un solo login: se gasta aquí aunque Face ID no esté
		// activado, o se quedaría para el siguiente y ese no pediría la cara (review de #574).
		const saltar = tocaSaltarFaceId();
		if (!biometriaActivada()) {
			return;
		}

		void tipoDeBiometria().then(setTipo);

		if (saltar) {
			return;
		}
		void intentar();
	}, [activo, intentar]);

	const entrarConBiometria = useCallback(() => {
		void intentar();
	}, [intentar]);

	return { tipo, entrarConBiometria };
};
